import { AtSign, BookHeart, Instagram, Music2 } from "lucide-react";
import { Badge } from "@/components/portal/Ui";

export const PLATFORMS = ["Instagram", "TikTok", "XiaoHongShu", "Threads"];

export const PLATFORM_META = {
  Instagram: { short: "IG", icon: Instagram, tint: "#C13584" },
  TikTok: { short: "TikTok", icon: Music2, tint: "#111111" },
  XiaoHongShu: { short: "XHS", icon: BookHeart, tint: "#D7263D" },
  Threads: { short: "Threads", icon: AtSign, tint: "#3D3D3D" },
};

export function PlatformIcon({ platform, className = "h-4 w-4" }) {
  const meta = PLATFORM_META[platform];
  if (!meta) return null;
  const Icon = meta.icon;
  return <Icon className={className} style={{ color: meta.tint }} aria-hidden />;
}

export function PlatformBadge({ platform, testId }) {
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full border border-[#E4DED3] bg-white px-2.5 py-1 text-[11px] font-bold uppercase tracking-[0.08em]"
      data-testid={testId}
    >
      <PlatformIcon platform={platform} className="h-3.5 w-3.5" />
      {PLATFORM_META[platform]?.short || platform}
    </span>
  );
}

const REVIEW_TONE = {
  Approved: "green",
  "Pending review": "orange",
  "Changes requested": "red",
};

export function ReviewBadge({ status, testId }) {
  return (
    <Badge tone={REVIEW_TONE[status] || "neutral"} testId={testId}>
      {status || "Approved"}
    </Badge>
  );
}

/** Labelled platform tabs with counts. Keyboard reachable, horizontally scrollable on mobile. */
export function PlatformTabs({ value, onChange, counts, idPrefix }) {
  const total = Object.values(counts).reduce((sum, n) => sum + n, 0);
  const tabs = [{ key: "all", label: "All platforms", count: total }].concat(
    PLATFORMS.map((platform) => ({ key: platform, label: PLATFORM_META[platform].short,
                                   count: counts[platform] || 0, platform }))
  );
  return (
    <div className="scroll-x -mx-1 px-1" role="tablist" aria-label="Filter submissions by platform">
      <div className="flex min-w-max gap-2">
        {tabs.map((tab) => {
          const active = value === tab.key;
          return (
            <button
              key={tab.key}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => onChange(tab.key)}
              data-testid={`${idPrefix}-platform-tab-${tab.key.toLowerCase()}`}
              className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-bold transition-colors ${
                active
                  ? "border-[#111111] bg-[#111111] text-white"
                  : "border-[#E4DED3] bg-white body-text hover:border-[#111111] hover:text-[#111111]"
              }`}
            >
              {tab.platform && (
                <PlatformIcon
                  platform={tab.platform}
                  className={`h-3.5 w-3.5 ${active ? "brightness-0 invert" : ""}`}
                />
              )}
              {tab.label}
              <span className={active ? "text-white/70" : "text-[#8a8378]"}>{tab.count}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export const UGC_CATEGORY = "UGC - Content Creation";

export function isUgc(category) {
  return category === UGC_CATEGORY;
}

export function CategoryBadge({ category, testId }) {
  if (!category) {
    return (
      <Badge tone="neutral" testId={testId}>
        Category not set
      </Badge>
    );
  }
  const short = category.startsWith("Both")
    ? "Review + Event"
    : category === "UGC - Content Creation"
      ? "UGC"
      : category;
  return (
    <Badge tone="orange" testId={testId}>
      {short}
    </Badge>
  );
}
